import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import SqlCodeBlock from "./SqlCodeBlock";
import { extractLastSqlFromMarkdown } from "../../utils/markdownParser";
import "./MarkdownRenderer.css";

// Helper function to extract text from React children
const extractTextFromChildren = (children) => {
  if (typeof children === "string") {
    return children;
  }
  if (Array.isArray(children)) {
    return children
      .map((child) => {
        if (typeof child === "string") return child;
        if (React.isValidElement(child)) {
          return extractTextFromChildren(child.props.children);
        }
        return "";
      })
      .join("");
  }
  if (React.isValidElement(children)) {
    return extractTextFromChildren(children.props.children);
  }
  return String(children);
};

// Generate a simple hash for SQL block key
const generateKey = (code) => {
  let hash = 0;
  for (let i = 0; i < code.length; i++) {
    const char = code.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `sql-${Math.abs(hash)}`;
};

// Helper function to check if content contains relationship discovery text
const isRelationshipDiscoveryContent = (text) => {
  if (!text || typeof text !== "string") return false;
  // Check for relationship discovery patterns
  return (
    (text.includes("Found") && text.includes("relationship") && text.includes("->")) ||
    (text.includes("Discovering relationships") && text.includes("->"))
  );
};

// Normalize SQL for comparison (trim + collapse whitespace) so we can match "last" block reliably
const normalizeSqlForCompare = (sql) => {
  if (!sql || typeof sql !== "string") return "";
  return sql.trim().replace(/\s+/g, " ");
};

const MarkdownRenderer = ({ content, question: questionProp, nlp_message_id: nlpMessageId, messageFeedback, messageModelId = null, onFeedbackSuccess, onRetry, isLastAssistantMessage = false, modelOptions = [], selectedModel = "", isLoading }) => {
  if (!content) {
    return null;
  }

  const question = questionProp ?? "";

  // Check if the entire content is relationship discovery text
  const isRelationshipContent = isRelationshipDiscoveryContent(content);

  // Precompute the last SQL block from raw markdown (same logic as extractLastSqlFromMarkdown)
  const lastSqlInContent = extractLastSqlFromMarkdown(content);
  const lastSqlNormalized = lastSqlInContent ? normalizeSqlForCompare(lastSqlInContent) : "";

  return (
    <div className="markdown-renderer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          p({ node, children, ...props }) {
            // Extract text from children to check if this paragraph contains relationship content
            const text = extractTextFromChildren(children);
            const isRelationship = isRelationshipDiscoveryContent(text) || isRelationshipContent;
            
            if (isRelationship) {
              // Apply white-space: pre-wrap to preserve newlines for relationship discovery content
              return (
                <p
                  {...props}
                  className="relationship-discovery"
                  style={{
                    margin: "12px 0",
                    lineHeight: "1.6",
                    color: "#475569",
                  }}
                >
                  {children}
                </p>
              );
            }
            
            // Default paragraph rendering
            return <p {...props}>{children}</p>;
          },
          pre({ node, children, ...props }) {
            // Check if this pre contains a SQL code block
            const childrenArray = React.Children.toArray(children);
            const hasSqlBlock = childrenArray.some((child) => {
              if (React.isValidElement(child) && child.type && child.type.name === "SqlCodeBlock") {
                return true;
              }
              if (React.isValidElement(child) && child.props && child.props.className && 
                  typeof child.props.className === "string" && child.props.className.includes("sql-code-block-wrapper")) {
                return true;
              }
              return false;
            });

            // If it contains SQL block, render without pre wrapper
            if (hasSqlBlock) {
              return <>{children}</>;
            }

            // Default pre rendering for other code blocks
            return (
              <pre
                style={{
                  background: "#f8fafc",
                  padding: "16px",
                  borderRadius: "4px",
                  overflow: "auto",
                  border: "1px solid #e1e5e9",
                  margin: "16px 0",
                }}
                {...props}
              >
                {children}
              </pre>
            );
          },
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "";
            
            // Properly extract code text from children (handles React elements, arrays, strings)
            const code = extractTextFromChildren(children).replace(/\n$/, "");

            // Use custom SQL code block component for SQL code
            if (!inline && language === "sql") {
              const sqlKey = generateKey(code);
              // Show thumb up/down only on the last SQL block (match by content, not render order)
              const codeNormalized = normalizeSqlForCompare(code);
              const showFeedbackIcons = lastSqlNormalized.length > 0 && codeNormalized === lastSqlNormalized;
              return (
                <SqlCodeBlock
                  key={sqlKey}
                  code={code}
                  language={language}
                  question={question}
                  showFeedbackIcons={showFeedbackIcons}
                  showRetryForThisMessage={isLastAssistantMessage}
                  nlp_message_id={nlpMessageId}
                  messageFeedback={messageFeedback}
                  messageModelId={messageModelId}
                  onFeedbackSuccess={onFeedbackSuccess}
                  onRetry={showFeedbackIcons ? onRetry : undefined}
                  modelOptions={modelOptions}
                  selectedModel={selectedModel}
                  isLoading={isLoading}
                  {...props}
                />
              );
            }

            // Default code block rendering for other languages
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;

